import { entries, map, sumBy, values, flow, filter, sortBy } from 'lodash/fp';

export function assessLimits(
  type,
  playerPositions = [],
  teamPositions = [],
  playerQuota = Infinity,
  teamQuota = Infinity,
  shareQuota = false
) {
  /*
   * A short explanation of these variables:
   * remainingTotalSelections: the difference between the total so far selected and the total
   * required.
   * remainingFreeSelections: the remaining selections that are not reserved for particular
   * positions.
   * For instance suppose I have BAT 4 (of 4-6) BOWL 4 (of 4-6) WK 0 (or 1) for a total of 8 (of 11)
   * Then I have three remaining selections, but one of these is allocated as a WK selection,
   * leaving only two "free" selections
   */

  const quota = type === 'player' ? playerQuota : teamQuota;
  const positions = type === 'player' ? playerPositions : teamPositions;

  const totalMaximum = shareQuota ? playerQuota : quota;
  const totalSelected = sumBy('selected')(positions);
  const totalAllocated = sumBy(pos => {
    const refPosition =
      pos.shareQuota && pos.isTeam
        ? playerPositions.find(p => p.id === pos.id && p.shareQuota)
        : pos;
    let positionSelected = pos.selected;

    if (pos.shareQuota) {
      const playerSelected = playerPositions.find(p => p.id === pos.id);
      const playerSelectedNum = playerSelected ? playerSelected.selected : 0;

      const teamSelected = teamPositions.find(p => p.id === pos.id);
      const teamSelectedNum = teamSelected ? teamSelected.selected : 0;
      positionSelected = playerSelectedNum + teamSelectedNum;
    }
    return Math.max(refPosition.min, positionSelected);
  })(positions);

  const remainingTotalSelections = totalMaximum - totalSelected;
  const remainingFreeSelections = Math.max(0, totalMaximum - totalAllocated);

  /*
   * A position is considered "filled" if:
   * a) The number of selections meets the minimum and there are no more free selections; or
   * b) The number of selections is at least as many as the maximum
   *
   * A position is in error if:
   * a) There are more selections than the maximum; or
   * b) There are fewer selections than the minimum and insufficient remaining selections to make
   * up the difference
   */

  return positions.map(pos => {
    const refPos =
      pos.shareQuota && pos.isTeam
        ? playerPositions.find(p => p.id === pos.id && p.shareQuota)
        : pos;
    let posSelected = pos.selected;

    if (shareQuota && pos.shareQuota) {
      const playerSelected = playerPositions.find(p => p.id === pos.id).selected;
      const teamSelected = teamPositions.find(p => p.id === pos.id).selected;
      posSelected = playerSelected + teamSelected;
    }

    return {
      ...pos,
      filled:
        (remainingFreeSelections === 0 && posSelected >= refPos.min) || posSelected >= refPos.max,
      hasError: posSelected > refPos.max || refPos.min - posSelected > remainingTotalSelections
    };
  });
}

export function getPositions(positions, entities, selections, entityType) {
  return map(pos => {
    const selectedPlayers = flow(
      map(([idStr, selection]) => ({
        ...entities[idStr.replace(`${entityType}_`, '')],
        ...selection,
        positionId: pos.id // store the positionId for autofill
      })),
      filter(e => e.id)
    )(entries(selections[pos.id]));

    return {
      ...pos,
      selected: selectedPlayers.length,
      selections: sortBy('gameOrder')(selectedPlayers)
    };
  }, values(positions));
}
